import axios from 'axios';
import configFile from '../config.js';

/**
 * Saved memories — short facts the signed-in person asked agents to remember
 * about them, carried into every agent run they make.
 *
 * Same shape as oauthApi: an axios instance against the BFF, authenticated with
 * the caller's real Keycloak token, with `accessToken` threaded in by the caller
 * because this module has no hook access of its own.
 *
 * `discoverUserId` is the Keycloak `sub`. It is sent, but it is not what decides
 * whose rows are touched: the BFF overwrites the field with the `sub` from the
 * verified token before forwarding. Passing it keeps the request well-formed for
 * a reader; trusting it would be the bug the BFF exists to prevent.
 */

const client = (accessToken) =>
  axios.create({
    baseURL: configFile.apiUrl,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });

// discover answers `{code, message, data}` on these routes; a non-zero code is a
// refusal with a message written for the person who typed the input (which limit
// was hit, why a memory was rejected), so it is surfaced rather than flattened.
const unwrap = (response) => {
  const body = response?.data;
  if (body?.code !== 0) {
    throw new Error(body?.message || 'Something went wrong.');
  }
  return body.data;
};

export const listMemories = async (accessToken, discoverUserId) =>
  unwrap(
    await client(accessToken).get('/user_memory', {
      params: { discover_user_id: discoverUserId },
    }),
  );

// `user_email` is sent so the BFF can REPLACE it with the verified value — its rule is
// replace-only, so a field the client omits is never injected. What we put here is
// therefore a placeholder, not a claim: discover stores whatever the token said. The
// display name is added by the BFF itself and is not sent from here at all.
export const createMemory = async (accessToken, discoverUserId, content, email) =>
  unwrap(
    await client(accessToken).post('/user_memory', {
      discover_user_id: discoverUserId,
      user_email: email || '',
      content,
    }),
  );

export const updateMemory = async (
  accessToken,
  discoverUserId,
  memoryId,
  content,
) =>
  unwrap(
    await client(accessToken).put(`/user_memory/${memoryId}`, {
      discover_user_id: discoverUserId,
      content,
    }),
  );

export const deleteMemory = async (accessToken, discoverUserId, memoryId) =>
  unwrap(
    // Query, not body: a DELETE body is not reliably forwarded, so the BFF route
    // and discover's handler both read the identity off the query string.
    await client(accessToken).delete(`/user_memory/${memoryId}`, {
      params: { discover_user_id: discoverUserId },
    }),
  );

export const getMemorySettings = async (accessToken, discoverUserId) =>
  unwrap(
    await client(accessToken).get('/user_memory/settings', {
      params: { discover_user_id: discoverUserId },
    }),
  );

// Either toggle alone; an omitted field leaves that one as it was, so the two
// switches never have to be sent together.
export const updateMemorySettings = async (
  accessToken,
  discoverUserId,
  patch,
  email,
) =>
  unwrap(
    await client(accessToken).put('/user_memory/settings', {
      discover_user_id: discoverUserId,
      user_email: email || '',
      ...patch,
    }),
  );
